import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';
import { Save, Send, Mail, Server, Lock, User, AtSign, Info, CheckCircle2 } from 'lucide-react';

export default function AdminSMTP() {
  const [form, setForm] = useState({ host: '', port: 587, username: '', password: '', fromEmail: '', fromName: 'Skillbridge.guru', useTls: true, enabled: false });
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    (async () => {
      try {
        const d = await api.admin.smtpGet();
        if (Object.keys(d).length) setForm(f => ({ ...f, ...d }));
      } catch {}
    })();
  }, []);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, port: Number(form.port) };
      // Don't send masked password back
      if (payload.password === '••••••••') delete payload.password;
      await api.admin.smtpUpdate(payload);
      toast({ title: 'SMTP settings saved' });
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail, variant: 'destructive' }); }
    setSaving(false);
  };

  const doTest = async () => {
    if (!testEmail) { toast({ title: 'Enter a test recipient email', variant: 'destructive' }); return; }
    setTesting(true);
    try {
      await api.admin.smtpTest(testEmail);
      toast({ title: 'Test email sent!', description: `Delivered to ${testEmail}` });
    } catch (e) {
      toast({ title: 'Test failed', description: e.response?.data?.detail || 'Check host, port, username, password.', variant: 'destructive' });
    }
    setTesting(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-extrabold">SMTP &amp; Email</h1>
        <p className="text-gray-500 text-sm mt-1">Configure the outbound email server used for auto-notifications (contact form, signups, enrollments).</p>
      </div>

      <div className="bg-white p-6 rounded-2xl border border-gray-200 space-y-4">
        <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-gray-100 hover:border-purple-200">
          <input type="checkbox" className="accent-[#6b47dc] w-5 h-5" checked={!!form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
          <div>
            <div className="font-bold">Enable outbound email</div>
            <div className="text-xs text-gray-500">When off, emails are silently skipped (site still works normally).</div>
          </div>
        </label>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold text-gray-500 mb-1 block">SMTP host</label>
            <div className="relative">
              <Server size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={form.host || ''} onChange={set('host')} placeholder="smtp.gmail.com" className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-gray-500 mb-1 block">Port</label>
            <input type="number" value={form.port || 587} onChange={set('port')} placeholder="587" className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-500 mb-1 block">Username</label>
            <div className="relative">
              <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={form.username || ''} onChange={set('username')} placeholder="you@gmail.com" className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-gray-500 mb-1 block">Password / App password</label>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="password" value={form.password || ''} onChange={set('password')} placeholder="••••••••" className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-gray-500 mb-1 block">From email</label>
            <div className="relative">
              <AtSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={form.fromEmail || ''} onChange={set('fromEmail')} placeholder="noreply@yourbusiness.com" className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-gray-500 mb-1 block">From name</label>
            <input value={form.fromName || ''} onChange={set('fromName')} placeholder="Skillbridge.guru Team" className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" />
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" className="accent-[#6b47dc]" checked={!!form.useTls} onChange={(e) => setForm({ ...form, useTls: e.target.checked })} /> Use STARTTLS (recommended)
        </label>

        <div className="flex gap-2">
          <Button onClick={save} disabled={saving} className="bg-[#6b47dc] hover:bg-[#5a3ac0]">
            <Save size={16} className="mr-1" /> {saving ? 'Saving...' : 'Save SMTP settings'}
          </Button>
        </div>
      </div>

      <div className="bg-gradient-to-br from-purple-50 to-orange-50 p-6 rounded-2xl border border-purple-100">
        <h2 className="font-bold text-lg mb-2 flex items-center gap-2"><Send size={18} className="text-[#6b47dc]" /> Send a test email</h2>
        <p className="text-xs text-gray-600 mb-3">Verify your credentials by sending a test message. Save settings first.</p>
        <div className="flex gap-2 flex-wrap">
          <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@example.com" className="flex-1 min-w-[240px] px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc] bg-white" />
          <Button onClick={doTest} disabled={testing} className="bg-[#ff5722] hover:bg-[#e64a19]">
            {testing ? 'Sending...' : <><CheckCircle2 size={16} className="mr-1" /> Send test email</>}
          </Button>
        </div>
      </div>

      <div className="bg-white p-5 rounded-2xl border border-gray-200 text-sm text-gray-600">
        <div className="flex items-start gap-2">
          <Info size={16} className="text-[#6b47dc] mt-0.5" />
          <div>
            <div className="font-bold text-gray-900 mb-1">Popular SMTP quick config</div>
            <div className="space-y-1">
              <div><b>Gmail:</b> <code>smtp.gmail.com : 587</code> &mdash; create an <a className="text-[#6b47dc] underline" href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer">App password</a> (2FA required).</div>
              <div><b>SendGrid:</b> <code>smtp.sendgrid.net : 587</code> &mdash; username <code>apikey</code>, password = your API key.</div>
              <div><b>Mailgun:</b> <code>smtp.mailgun.org : 587</code> &mdash; SMTP creds from Mailgun dashboard.</div>
              <div><b>AWS SES:</b> <code>email-smtp.&lt;region&gt;.amazonaws.com : 587</code> &mdash; SMTP creds from SES.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
